import { toolboxApi, unwrapResult } from '~/modules/query/base';

/** GET /listing-videos/stats — counters keyed by stat name. */
export interface VideoStats {
  [key: string]: unknown;
}

/** One point of a historical series; `_id` is a MM-DD-YYYY date. */
export interface VideoSeriesPoint {
  _id: string;
  count: number;
}

/** GET /listing-videos/historical-{listing-types,dimensions}-jobs — {seriesKey: points}. */
export type VideoHistoricalSeries = Record<string, VideoSeriesPoint[]>;

export interface VideoErrorPieEntry {
  count?: number;
  percent?: number;
}

/** GET /listing-videos/pie-chart — per-errorCode entries plus a `totalCount`. */
export interface VideoErrorPie {
  totalCount?: number;
  [errorCode: string]: VideoErrorPieEntry | number | undefined;
}

/** One row of the failed / queued job lists; the service sends more fields than the page reads. */
export interface VideoJob {
  _id?: string;
  status?: string;
  receivedTime?: string;
  template?: string;
  errorCode?: string | number;
  error?: { message?: string };
  data?: {
    mls_number?: string;
    listing_id?: string;
    listing_address?: string;
    account_id?: string;
    created_by?: string;
  };
  [key: string]: unknown;
}

export interface VideoSeriesArgs {
  type?: string;
  daysAgo?: number;
}

const DEFAULT_VIDEO_TYPES = 'listing_video,unbranded_listing_video';
const DEFAULT_DAYS_AGO = 14;

const seriesParams = ({
  type = DEFAULT_VIDEO_TYPES,
  daysAgo = DEFAULT_DAYS_AGO,
}: VideoSeriesArgs) => ({ type, daysAgo });

export const errorPieParams = ({ videoType = DEFAULT_VIDEO_TYPES }: { videoType?: string }) => ({
  field: 'errorCode',
  status: 'Error',
  videoType,
});

export const listingVideosApi = toolboxApi.injectEndpoints({
  endpoints: (build) => ({
    getVideoStats: build.query<VideoStats, void>({
      query: () => ({ url: '/listing-videos/stats', method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<VideoStats>(body),
      providesTags: ['VideoStats'],
    }),

    getListingTypeSeries: build.query<VideoHistoricalSeries, VideoSeriesArgs>({
      query: (args) => ({
        url: '/listing-videos/historical-listing-types-jobs',
        method: 'GET',
        params: seriesParams(args),
      }),
      transformResponse: (body: unknown) => unwrapResult<VideoHistoricalSeries>(body),
      providesTags: ['VideoChart'],
    }),

    getDimensionSeries: build.query<VideoHistoricalSeries, VideoSeriesArgs>({
      query: (args) => ({
        url: '/listing-videos/historical-dimensions-jobs',
        method: 'GET',
        params: seriesParams(args),
      }),
      transformResponse: (body: unknown) => unwrapResult<VideoHistoricalSeries>(body),
      providesTags: ['VideoChart'],
    }),

    getVideoErrorPie: build.query<VideoErrorPie, { videoType?: string }>({
      query: (args) => ({
        url: '/listing-videos/pie-chart',
        method: 'GET',
        params: errorPieParams(args),
      }),
      transformResponse: (body: unknown) => unwrapResult<VideoErrorPie>(body),
      providesTags: ['VideoChart'],
    }),

    getVideoFailedJobs: build.query<VideoJob[], void>({
      query: () => ({ url: '/listing-videos/get-failed-jobs', method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<VideoJob[]>(body),
      providesTags: ['VideoJob'],
    }),

    getVideoQueuedJobs: build.query<VideoJob[], void>({
      query: () => ({ url: '/listing-videos/get-queued-or-processing-jobs', method: 'GET' }),
      transformResponse: (body: unknown) => unwrapResult<VideoJob[]>(body),
      providesTags: ['VideoJob'],
    }),
  }),
});

export const {
  useGetVideoStatsQuery,
  useGetListingTypeSeriesQuery,
  useGetDimensionSeriesQuery,
  useGetVideoErrorPieQuery,
  useGetVideoFailedJobsQuery,
  useGetVideoQueuedJobsQuery,
} = listingVideosApi;
